"use client";
import React from "react";
import { motion } from "framer-motion";
import { IoArrowBack, IoClose } from "react-icons/io5";
import { FiShare } from "react-icons/fi";
import { CiEdit } from "react-icons/ci";

const CURRENCY_SYMBOL_MAP = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  BRL: "R$",
  MXN: "$",
  AED: "د.إ",
  ARS: "$",
  ZAR: "R",
  ILS: "₪",
};

const getCurrencySymbol = (currencyCode) => {
  return CURRENCY_SYMBOL_MAP[currencyCode] || currencyCode || "$";
};

export default function InvoicePreviewModal({ invoice, onBack, onClose }) {
  const bank = invoice?.bank_info || {};
  const customer = invoice?.customer_details || {};
  const services = invoice?.services_details || [];
  const listings = invoice?.listings_details || [];
  
  const hasOtherService = invoice?.other_service || false;
  const otherServicePrice = invoice?.other_service_price || 0;
  
  const otherService = hasOtherService ? [{
    id: 'other_service',
    name: "Other Service",
    price: otherServicePrice,
    currency: invoice?.currency || "USD",
    type: "OTHER"
  }] : [];

  React.useEffect(() => {
  }, [invoice, services, listings, hasOtherService, otherServicePrice]);

  const getInvoiceCurrency = () => {
    if (services.length > 0 && services[0].currency) {
      return services[0].currency;
    }
    if (listings.length > 0 && listings[0].currency) {
      return listings[0].currency;
    }
    return invoice?.currency || "USD";
  };

  const invoiceCurrency = getInvoiceCurrency();
  const currencySymbol = getCurrencySymbol(invoiceCurrency);

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return value;
    }
  };

  const getPaymentDueDate = () => {
    if (!invoice?.invoice_date || !invoice?.payment_due) return "-";
    
    try {
      const invoiceDate = new Date(invoice.invoice_date);
      const dueDays = parseInt(invoice.payment_due);
      
      if (isNaN(invoiceDate.getTime()) || isNaN(dueDays)) return "-";
      
      const dueDate = new Date(invoiceDate);
      dueDate.setDate(dueDate.getDate() + dueDays);
      
      return dueDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      console.error("Error calculating payment due date:", e);
      return "-";
    }
  };

  const handleShare = () => {
    if (invoice?.pdf_file_link) {
      window.open(invoice.pdf_file_link, "_blank", "noopener,noreferrer");
    } else {
      alert("PDF link is not available yet");
    }
  };

  const serviceTotal = invoice?.service_total ?? 0;
  const taxAmount = invoice?.tax_amount ?? 0;
  const discountAmount = invoice?.discount_amount ?? 0;
  const total = invoice?.calculated_total_price ?? 0;

  const formatPrice = (price, currency = invoiceCurrency) => {
    const symbol = getCurrencySymbol(currency);
    const numPrice = Number(price);
    if (isNaN(numPrice)) return `${symbol}0.00`;
    return `${symbol}${numPrice.toFixed(2)}`;
  };

  const formatTablePrice = (price) => {
    const numPrice = Number(price);
    if (isNaN(numPrice)) return "0.00";
    return numPrice.toFixed(2);
  };

  const allItems = [
    ...(services || []).map(item => ({ 
      ...item, 
      type: "SERVICE",
      displayName: item.name || "Service",
      price: item.price || 0,
      currency: item.currency || invoiceCurrency
    })),
    ...(listings || []).map(item => ({ 
      ...item, 
      type: "LISTING",
      displayName: item.property_name || "Listing",
      price: item.sell_price || item.monthly_rent_price || item.price || 0,
      currency: item.currency || invoiceCurrency,
      listing_type: item.listing_type
    })),
    ...(otherService || []).map((item) => ({
      ...item,
      displayName: item.name || "Other Service",
      price: item.price || 0,
      currency: item.currency || invoiceCurrency
    }))
  ];

  const getColumnHeader = () => {
    const hasServices = services.length > 0;
    const hasListings = listings.length > 0;
    const hasOther = hasOtherService;
    
    if (hasServices && hasListings && hasOther) {
      return "Service/Listing/Custom";
    } else if (hasServices && hasListings) {
      return "Service/Listing";
    } else if (hasServices && hasOther) {
      return "Service/Custom";
    } else if (hasListings && hasOther) {
      return "Listing/Custom";
    } else if (hasServices) {
      return "Service";
    } else if (hasListings) {
      return "Listing";
    } else if (hasOther) {
      return "Custom Service";
    } else {
      return "Item";
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="bg-white w-[800px] max-w-full rounded-3xl border border-[rgba(154,153,171,0.2)] shadow-lg p-6 flex flex-col items-center relative"
      >
        <div className="flex justify-between items-center w-full">
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-[#302B2B] hover:text-[#5563F6]"
          >
            <IoArrowBack size={20} /> Back
          </button>
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center text-gray-600 hover:text-black"
          >
            <IoClose size={22} />
          </button>
        </div>
        <h2 className="text-[20px] font-bold text-[#302B2B] mb-4">Invoice Preview</h2>

        <div className="border-2 border-[#E2E3F1] rounded-2xl p-5 flex flex-col gap-5 w-full max-h-[520px] overflow-y-auto bg-[#F9FAFF]">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px] bg-white rounded-2xl border border-[#EDEEF3] p-4">
              <h3 className="text-sm font-semibold text-[#302B2B] mb-2">
                From
              </h3>
              <p className="text-[14px] font-semibold text-[#302B2B]">
                {bank.company_name || bank.name_surname || "-"}
              </p>
              {bank.name_surname && bank.company_name && (
                <p className="text-[12px] text-[#9A99AB] mt-0.5">
                  {bank.name_surname}
                </p>
              )}
              {bank.address && (
                <p className="text-[12px] text-[#302B2B] mt-2">
                  {bank.address}
                </p>
              )}
              <div className="mt-3 space-y-1 text-[12px] text-[#302B2B]">
                {bank.phone_number && (
                  <p>
                    <span className="font-medium">Phone: </span>
                    {bank.phone_number}
                  </p>
                )}
                {bank.email && (
                  <p>
                    <span className="font-medium">Email: </span>
                    {bank.email}
                  </p>
                )}
              </div>
              <div className="mt-3 space-y-1 text-[12px] text-[#302B2B]">
                {bank.iban && (
                  <p>
                    <span className="font-medium">IBAN: </span>
                    {bank.iban}
                  </p>
                )}
                {bank.swift_bic && (
                  <p>
                    <span className="font-medium">SWIFT/BIC: </span>
                    {bank.swift_bic}
                  </p>
                )}
              </div>
            </div>

            <div className="flex-1 min-w-[260px] bg-white rounded-2xl border border-[#EDEEF3] p-4">
              <h3 className="text-sm font-semibold text-[#302B2B] mb-2">
                Bill to
              </h3>
              <div className="flex items-center gap-3">
                {customer.image_url && (
                  <img
                    src={customer.image_url}
                    alt={customer.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                )}
                <div>
                  <p className="text-[14px] font-semibold text-[#302B2B]">
                    {customer.name || "-"}
                  </p>
                  {customer.company_name && (
                    <p className="text-[12px] text-[#9A99AB]">
                      {customer.company_name}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-3 space-y-1 text-[12px] text-[#302B2B]">
                {customer.tax_id && (
                  <p>
                    <span className="font-medium">TAX ID: </span>
                    {customer.tax_id}
                  </p>
                )}
                {customer.email && (
                  <p>
                    <span className="font-medium">Email: </span>
                    {customer.email}
                  </p>
                )}
                {customer.phone_number && (
                  <p>
                    <span className="font-medium">Phone: </span>
                    {customer.phone_number}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#EDEEF3] p-4 flex flex-wrap gap-6 text-[13px] text-[#302B2B]">
            <div>
              <p className="font-semibold text-xs text-[#9A99AB]">
                Invoice Number
              </p>
              <p className="mt-1 font-medium">{invoice?.invoice_number || "-"}</p>
            </div>
            <div>
              <p className="font-semibold text-xs text-[#9A99AB]">
                Project Name
              </p>
              <p className="mt-1 font-medium">{invoice?.project_name || "-"}</p>
            </div>
            <div>
              <p className="font-semibold text-xs text-[#9A99AB]">
                Invoice Date
              </p>
              <p className="mt-1 font-medium">{formatDate(invoice?.invoice_date)}</p>
            </div>
            <div>
              <p className="font-semibold text-xs text-[#9A99AB]">
                Payment Due Date
              </p>
              <p className="mt-1 font-medium">{getPaymentDueDate()}</p>
            </div>
            <div>
              <p className="font-semibold text-xs text-[#9A99AB]">
                Payment Term
              </p>
              <p className="mt-1 font-medium">
                {invoice?.payment_due ? `Within ${invoice.payment_due} days` : "-"}
              </p>
            </div>
            <div>
              <p className="font-semibold text-xs text-[#9A99AB]">
                Status
              </p>
              <p className="mt-1 font-medium">
                <span className={`px-2 py-1 rounded text-xs ${
                  invoice?.paid 
                    ? "bg-green-100 text-green-800" 
                    : "bg-yellow-100 text-yellow-800"
                }`}>
                  {invoice?.paid ? "Paid" : "Pending"}
                </span>
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#EDEEF3] p-4">
            {allItems.length === 0 ? (
              <p className="text-[12px] text-[#9A99AB] text-center py-4">No items added.</p>
            ) : (
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[#9A99AB] border-b border-[#EDEEF3]">
                    <th className="py-3 font-medium">{getColumnHeader()}</th>
                    <th className="py-3 font-medium text-right">Price</th>
                    <th className="py-3 font-medium text-right">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {allItems.map((item, index) => (
                    <tr
                      key={`${item.type}-${item.id || index}`}
                      className="border-b border-[#F1F2FA] last:border-b-0 hover:bg-gray-50"
                    >
                      <td className="py-3 pr-3 text-[#302B2B]">
                        <div>
                          <div className="font-medium">{item.displayName}</div>
                          {item.listing_type && (
                            <div className="text-xs text-[#9A99AB] mt-1">
                              Type: {item.listing_type}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 text-right text-[#302B2B] font-medium">
                        {getCurrencySymbol(item.currency)}{formatTablePrice(item.price)}
                      </td>
                      <td className="py-3 text-right text-[#302B2B]">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          item.type === "SERVICE" 
                            ? "bg-blue-100 text-blue-800" 
                            : item.type === "LISTING" 
                            ? "bg-green-100 text-green-800" 
                            : "bg-purple-100 text-purple-800"
                        }`}>
                          {item.type}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex justify-end">
            <div className="w-full max-w-[320px] bg-white rounded-2xl border border-[#EDEEF3] p-4 text-[13px] text-[#302B2B] space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-[#9A99AB]">Subtotal:</span>
                <span className="font-medium">{formatPrice(serviceTotal)}</span>
              </div>
              
              {hasOtherService && (
                <div className="flex justify-between items-center">
                  <span className="text-[#9A99AB]">Other Service:</span>
                  <span className="font-medium">{formatPrice(otherServicePrice)}</span>
                </div>
              )}
              
              <div className="flex justify-between items-center">
                <span className="text-[#9A99AB]">Tax ({invoice?.tax ?? 0}%):</span>
                <span className="font-medium">{formatPrice(taxAmount)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-[#9A99AB]">Discount ({invoice?.discount ?? 0}%):</span>
                <span className="font-medium text-red-600">-{formatPrice(discountAmount)}</span>
              </div>
              
              <div className="h-[1px] bg-[#EDEEF3] my-2" />
              
              <div className="flex justify-between items-center text-[15px] font-bold">
                <span>Total Amount:</span>
                <span className="text-[#5563F6]">{formatPrice(total)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center w-full mt-6">
          <button
            onClick={handleShare}
            className="border border-[#EDEEF3] rounded-[14px] px-6 py-3 hover:bg-[#F6F6F6] text-[#302B2B] font-medium flex items-center justify-center gap-2 transition"
          >
            <FiShare /> Share
          </button>

          <div className="flex gap-3">
            <button
              onClick={onBack}
              className="border border-[#EDEEF3] rounded-[14px] px-6 py-3 hover:bg-[#F6F6F6] text-[#302B2B] font-medium flex items-center justify-center gap-2 transition"
            >
              <CiEdit /> Edit
            </button>

            <button
              onClick={onClose}
              className="bg-[#5563F6] text-white rounded-[14px] px-6 py-3 hover:bg-[#3f4de0] font-medium flex items-center justify-center gap-2 transition"
            >
              Create Invoice
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}